import { ReactComponent as CreatorIcon } from '../assets/icons/creator-icon.svg';
import { ReactComponent as TagsIcon } from '../assets/icons/tags-icon.svg';
import { ReactComponent as TrackIcon } from '../assets/icons/track-icon.svg';
import { ReactComponent as DivisionIcon } from '../assets/icons/account_tree.svg';
import { ReactComponent as DepartmentIcon } from '../assets/icons/apartment.svg';
import { ReactComponent as LevelIcon } from '../assets/icons/track-level-icon.svg';
import { ReactComponent as JobTitleIcon } from '../assets/icons/job-title-icon.svg';
import { ReactComponent as RoleIcon } from '../assets/icons/badge.svg';
import {
  NOTE_TYPES_MAP,
  USER_EMPLOYMENT_INFORMATION,
  USER_BASIC_PERSONAL_INFORMATION,
  USER_FIELDS,
  LANGUAGES,
  GENDER_OPTIONS,
  SENSITIVE_DATA_DEFAULT,
} from '../constants/people';
import { FIELD_TYPES } from '../constants/fieldTypes';
import { SYSTEM_ROLES } from '../constants/roles';
import { USER_STATUSES, USER_STATUSES_MAP } from '../constants/statuses';
import { PARAMS } from '../constants/pages';
import { filterTags } from './tagUtils';
import {
  prepareTracksWithLevels,
  prepareDivisionsWithDepartments,
  prepareAllTracksLevels,
  prepareFilterableUsers,
} from './uiUtils';
import { prepareSystemRoleFilters, systemRoleCheck } from './roles';
import { getItemById, isObject } from './helpers';
import { isEmpty } from './validation';

const {
  REPORT_TO,
  JOB_TITLE,
  FRAMEWORK,
  TRACK,
  LEVEL,
  DIVISION,
  DEPARTMENT,
  WORKING_HOURS,
  NET_SALARY,
  GROSS_SALARY_1,
  GROSS_SALARY_2,
  EMPLOYMENT_DATE,
  PRIVATE_HEALTH_INSURANCE,
} = USER_EMPLOYMENT_INFORMATION;

const {
  FIRST_NAME,
  LAST_NAME,
  EMAIL,
  GENDER,
  DATE_OF_BIRTH,
  PLACE_OF_BIRTH,
  RESIDENTIAL_ADDRESS,
  ID_NUMBER,
  NATIONAL_ID_NUMBER,
  PROFESSION_TITLE,
  YEARS_OF_EXPERIENCE,
  EXPERIENCE,
  EDUCATION,
  SOCIAL_ACCOUNTS,
  EXTERNAL_RESOURCE,
  LANGUAGE,
  TIME_ZONE,
} = USER_BASIC_PERSONAL_INFORMATION;

const { SUBTITLE } = FIELD_TYPES;

export const prepareUserStatuses = (labels, shouldRemoveDeactivated = false) =>
  USER_STATUSES_MAP.reduce((acc, status) => {
    if (shouldRemoveDeactivated && status.id === USER_STATUSES.DEACTIVATED)
      return acc;

    return [
      ...acc,
      {
        ...status,
        name: labels[status.key],
      },
    ];
  }, []);

export const getPeopleNoteTypes = labels =>
  NOTE_TYPES_MAP.map(type => ({
    ...type,
    name: labels[type.key].name,
    description: labels[type.key].description,
  }));

export const preparePeopleFilters = (
  translations,
  jobTitles,
  tracks,
  divisions,
  departments,
  userRoles,
  allUsers = [],
  tagCategories = [],
  shouldRemoveAllUsersFilter = false,
  shouldRemoveTagsFilter = false
) => {
  const {
    ID,
    ROLE: ROLE_FILTER,
    JOB_TITLE: JOB_TITLE_FILTER,
    TAGS,
    TRACK: TRACK_FILTER,
    LEVEL: LEVEL_TRACK_FILTER,
    DIVISION: DIVISION_FILTER,
    DEPARTMENT: DEPARTMENT_FILTER,
  } = PARAMS;
  return [
    ...(shouldRemoveAllUsersFilter
      ? []
      : [
          {
            id: ID,
            name: translations.filters.labels.employee,
            icon: CreatorIcon,
            items: prepareFilterableUsers(allUsers),
          },
        ]),
    {
      id: ROLE_FILTER,
      name: translations.filters.labels[ROLE_FILTER],
      icon: RoleIcon,
      items: prepareSystemRoleFilters(userRoles),
    },
    {
      id: JOB_TITLE_FILTER,
      name: translations.filters.labels[JOB_TITLE_FILTER],
      icon: JobTitleIcon,
      items: jobTitles,
    },
    {
      id: TRACK_FILTER,
      name: translations.filters.labels.track,
      icon: TrackIcon,
      items: prepareTracksWithLevels(tracks),
    },
    {
      id: LEVEL_TRACK_FILTER,
      name: translations.filters.labels.trackLevel,
      icon: LevelIcon,
      items: prepareAllTracksLevels(tracks),
    },
    {
      id: DIVISION_FILTER,
      name: translations.filters.labels.division,
      icon: DivisionIcon,
      items: prepareDivisionsWithDepartments(divisions),
    },
    {
      id: DEPARTMENT_FILTER,
      name: translations.filters.labels.department,
      icon: DepartmentIcon,
      items: departments,
    },
    ...(shouldRemoveTagsFilter
      ? []
      : [
          {
            id: TAGS,
            name: translations.filters.labels[TAGS],
            icon: TagsIcon,
            categoryItemsKey: TAGS,
            hasCategoryColorBox: true,
            hasSubmenuHash: true,
            isCategorized: true,
            items: tagCategories,
            categorySearch: filterTags,
          },
        ]),
  ];
};

export const prepareTimezones = () => {
  const timezones = Intl.supportedValuesOf('timeZone');

  return timezones.map(tz => {
    const now = new Date();
    const formattedTimezone = tz?.replace(/_/g, ' ');

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    });

    const parts = formatter.formatToParts(now);
    const offset = parts.find(p => p.type === 'timeZoneName')?.value || 'GMT';

    return {
      value: formattedTimezone,
      label: `(${offset}) ${formattedTimezone}`,
    };
  });
};

export const prepareHealthInsuranceValue = value => (value ? 'Yes' : 'No');

const prepareSensitiveAwareValue = (
  value,
  fieldName,
  fieldsSettings,
  canViewSensitiveData = false
) =>
  fieldsSettings[fieldName]?.is_sensitive && !canViewSensitiveData
    ? SENSITIVE_DATA_DEFAULT
    : value;

const getOptionFieldValue = (
  user,
  fieldName,
  fieldsSettings,
  defaultValue,
  options,
  key = '',
  canViewSensitiveData = false
) => {
  if (fieldsSettings[fieldName]?.is_sensitive && !canViewSensitiveData) {
    return SENSITIVE_DATA_DEFAULT;
  }

  return getItemById(options, user[fieldName], key) || defaultValue;
};

const getFieldValue = (
  user,
  fieldName,
  fieldsSettings,
  defaultValue,
  canViewSensitiveData = false
) => {
  if (fieldsSettings[fieldName]?.is_sensitive && !canViewSensitiveData) {
    return SENSITIVE_DATA_DEFAULT;
  }

  return user[fieldName] || defaultValue;
};

const isFieldVisibleInSettings = (
  fieldsSettings,
  fieldName,
  canViewSensitiveData = false
) =>
  fieldsSettings[fieldName]?.is_visible &&
  (!fieldsSettings[fieldName]?.is_sensitive || canViewSensitiveData);

const createSubtitle = name => ({
  name,
  type: SUBTITLE,
});

export const prepareInitialUserData = (
  user,
  canViewSensitiveData,
  fieldsSettings
) => {
  const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneList = prepareTimezones();

  return {
    [FIRST_NAME]: getFieldValue(
      user,
      FIRST_NAME,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [LAST_NAME]: getFieldValue(
      user,
      LAST_NAME,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [EMAIL]: getFieldValue(
      user,
      EMAIL,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [GENDER]: getOptionFieldValue(
      user,
      GENDER,
      fieldsSettings,
      null,
      GENDER_OPTIONS,
      'name',
      canViewSensitiveData
    ),
    [DATE_OF_BIRTH]: getFieldValue(
      user,
      DATE_OF_BIRTH,
      fieldsSettings,
      null,
      canViewSensitiveData
    ),
    [PLACE_OF_BIRTH]: getFieldValue(
      user,
      PLACE_OF_BIRTH,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [RESIDENTIAL_ADDRESS]: getFieldValue(
      user,
      RESIDENTIAL_ADDRESS,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [ID_NUMBER]: getFieldValue(
      user,
      ID_NUMBER,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [NATIONAL_ID_NUMBER]: getFieldValue(
      user,
      NATIONAL_ID_NUMBER,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [PROFESSION_TITLE]: getFieldValue(
      user,
      PROFESSION_TITLE,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [YEARS_OF_EXPERIENCE]: getFieldValue(
      user,
      YEARS_OF_EXPERIENCE,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [EXPERIENCE]: getFieldValue(
      user,
      EXPERIENCE,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [SOCIAL_ACCOUNTS]: getFieldValue(
      user,
      SOCIAL_ACCOUNTS,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [EDUCATION]: getFieldValue(
      user,
      EDUCATION,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [EXTERNAL_RESOURCE]: getFieldValue(
      user,
      EXTERNAL_RESOURCE,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [LANGUAGE]: getOptionFieldValue(
      user,
      LANGUAGE,
      fieldsSettings,
      null,
      LANGUAGES,
      'value',
      canViewSensitiveData
    ),
    [TIME_ZONE]:
      getOptionFieldValue(
        user,
        TIME_ZONE,
        fieldsSettings,
        null,
        timezoneList,
        'value',
        canViewSensitiveData
      ) ||
      getItemById(timezoneList, currentTimezone, 'value') ||
      null,
  };
};

export const prepareEditPersonalInformationFields = (
  user,
  canViewSensitiveData,
  fieldsSettings
) => {
  const isAssociate = systemRoleCheck(user, SYSTEM_ROLES.ASSOCIATE);
  const isVisible = fieldName =>
    isFieldVisibleInSettings(fieldsSettings, fieldName, canViewSensitiveData);
  const includeField = field => isVisible(field.name);
  const toFieldConfig = field => ({ ...field });

  const basicFields = [
    USER_FIELDS.FIRST_NAME,
    USER_FIELDS.LAST_NAME,
    USER_FIELDS.EMAIL,
  ]
    .filter(includeField)
    .map(toFieldConfig);

  if (isAssociate) {
    return basicFields;
  }

  const personalDetailFields = [
    USER_FIELDS.GENDER,
    USER_FIELDS.DATE_OF_BIRTH,
    USER_FIELDS.PLACE_OF_BIRTH,
  ]
    .filter(includeField)
    .map(toFieldConfig);

  const preferenceFields = [USER_FIELDS.LANGUAGE, USER_FIELDS.TIME_ZONE]
    .filter(includeField)
    .map(toFieldConfig);

  const identificationFields = [
    USER_FIELDS.ID_NUMBER,
    USER_FIELDS.NATIONAL_ID_NUMBER,
    USER_FIELDS.RESIDENTIAL_ADDRESS,
  ]
    .filter(includeField)
    .map(toFieldConfig);

  const professionalFields = [
    USER_FIELDS.PROFESSION_TITLE,
    USER_FIELDS.EDUCATION,
    USER_FIELDS.YEARS_OF_EXPERIENCE,
    USER_FIELDS.EXPERIENCE,
  ]
    .filter(includeField)
    .map(toFieldConfig);

  const additionalFields = [
    USER_FIELDS.SOCIAL_ACCOUNTS,
    USER_FIELDS.EXTERNAL_RESOURCE,
  ]
    .filter(includeField)
    .map(toFieldConfig);

  return [
    ...(basicFields.length ? [createSubtitle('basic'), ...basicFields] : []),
    ...(personalDetailFields.length
      ? [createSubtitle('personalDetails'), ...personalDetailFields]
      : []),
    ...(preferenceFields.length
      ? [createSubtitle('preferences'), ...preferenceFields]
      : []),
    ...(identificationFields.length
      ? [createSubtitle('identification'), ...identificationFields]
      : []),
    ...(professionalFields.length
      ? [createSubtitle('professionalBackground'), ...professionalFields]
      : []),
    ...(additionalFields.length
      ? [createSubtitle('additionalInformation'), ...additionalFields]
      : []),
  ];
};

export const prepareEditEmploymentInformationFields = (
  classes,
  canViewSensitiveData,
  fieldsSettings
) => {
  const isVisible = fieldName =>
    isFieldVisibleInSettings(fieldsSettings, fieldName, canViewSensitiveData);
  const includeField = field => isVisible(field.name);

  const organizationFields = [
    USER_FIELDS.JOB_TITLE,
    {
      ...USER_FIELDS.REPORT,
      validators: [
        {
          validator: isEmpty,
          type: 'required',
        },
      ],
    },
    USER_FIELDS.DIVISION,
    USER_FIELDS.DEPARTMENT,
    USER_FIELDS.FRAMEWORK,
    USER_FIELDS.TRACK,
    USER_FIELDS.LEVEL,
  ].filter(includeField);

  const employmentDetailFields = [
    USER_FIELDS.EMPLOYMENT_DATE,
    USER_FIELDS.WORKING_HOURS,
  ]
    .filter(includeField)
    .map(field => ({ ...field }));

  const compensationFields = [
    USER_FIELDS.NET_SALARY,
    USER_FIELDS.GROSS_SALARY_1,
    USER_FIELDS.GROSS_SALARY_2,
  ]
    .filter(includeField)
    .map(field => ({ ...field }));

  const additionalFields = isVisible(USER_FIELDS.PRIVATE_HEALTH_INSURANCE.name)
    ? [
        {
          ...USER_FIELDS.PRIVATE_HEALTH_INSURANCE,
          labelClass: classes.switchLabel,
        },
      ]
    : [];

  return [
    ...(organizationFields.length
      ? [createSubtitle('organization'), ...organizationFields]
      : []),
    ...(employmentDetailFields.length
      ? [createSubtitle('employmentDetails'), ...employmentDetailFields]
      : []),
    ...(compensationFields.length
      ? [createSubtitle('compensation'), ...compensationFields]
      : []),
    ...(additionalFields.length
      ? [createSubtitle('additionalInformation'), ...additionalFields]
      : []),
  ];
};

export const prepareInitialEmploymentUserData = (
  user,
  canViewSensitiveData,
  tracks,
  frameworks,
  fieldsSettings,
  reportTo = null
) => {
  const isTrackId = !isObject(user[TRACK]) && user[TRACK];
  const userTrack = isTrackId
    ? tracks.find(track => track.id === user[TRACK])
    : user[TRACK];
  const userFramework = isObject(userTrack)
    ? frameworks.find(framework => framework.id === userTrack.framework)
    : {};

  return {
    [WORKING_HOURS]: getFieldValue(
      user,
      WORKING_HOURS,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [NET_SALARY]: getFieldValue(
      user,
      NET_SALARY,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [GROSS_SALARY_1]: getFieldValue(
      user,
      GROSS_SALARY_1,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [GROSS_SALARY_2]: getFieldValue(
      user,
      GROSS_SALARY_2,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [PRIVATE_HEALTH_INSURANCE]: getFieldValue(
      user,
      PRIVATE_HEALTH_INSURANCE,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [JOB_TITLE]: prepareSensitiveAwareValue(
      user[JOB_TITLE]?.id,
      JOB_TITLE,
      fieldsSettings,
      canViewSensitiveData
    ),
    [EMPLOYMENT_DATE]: getFieldValue(
      user,
      EMPLOYMENT_DATE,
      fieldsSettings,
      null,
      canViewSensitiveData
    ),
    [FRAMEWORK]: prepareSensitiveAwareValue(
      userFramework?.id,
      FRAMEWORK,
      fieldsSettings,
      canViewSensitiveData
    ),
    [TRACK]: prepareSensitiveAwareValue(
      userTrack?.id || userTrack,
      TRACK,
      fieldsSettings,
      canViewSensitiveData
    ),
    [LEVEL]: prepareSensitiveAwareValue(
      user[LEVEL]?.id || user[LEVEL],
      LEVEL,
      fieldsSettings,
      canViewSensitiveData
    ),
    [DIVISION]: getFieldValue(
      user,
      DIVISION,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [DEPARTMENT]: getFieldValue(
      user,
      DEPARTMENT,
      fieldsSettings,
      '',
      canViewSensitiveData
    ),
    [REPORT_TO]: prepareSensitiveAwareValue(
      reportTo?.id || user[REPORT_TO]?.id,
      REPORT_TO,
      fieldsSettings,
      canViewSensitiveData
    ),
  };
};

export const getFieldSettingsMap = userInfo => {
  return userInfo.reduce((acc, uInfo) => {
    return {
      ...acc,
      [uInfo.name]: {
        is_visible: uInfo.is_visible,
        is_sensitive: uInfo.is_sensitive,
      },
    };
  }, {});
};
